import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api, qs } from '../lib/api.js';
import { BrandSelect } from '../lib/brands.js';
import { dateTime, num } from '../lib/format.js';
import { TX_STATUS_LABEL, label } from '../lib/labels.js';
import { ErrorState, Loading, Panel, Tile } from '../components/ui.js';

interface TxStats {
  windowHours: number;
  accepted: number;
  sent: number;
  held: number;
  failed: number;
  unknown: number;
  shadow: number;
  queued: number;
  stuck: number;
  latencyP50Ms: number | null;
  latencyP95Ms: number | null;
}

interface CredentialCode {
  code: string;
  name: string;
  total: number;
  previousTotal: number;
  trend: 'up' | 'down' | 'flat';
  byStatus: Record<string, number>;
  byDay: Array<{ date: string; count: number }>;
  lastAt: string | null;
  repeated: Array<{ recipient: string; count: number; lastAt: string }>;
}

const lag = (ms: number | null): string =>
  ms === null ? '—' : ms < 1000 ? `${ms} ms` : `${(ms / 1000).toFixed(1).replace('.', ',')} s`;

const TREND_TEXT = { up: 'více než minule', down: 'méně než minule', flat: 'stejně jako minule' };

/** Transakční zprávy → Sledování: čísla za 24 hodin a kdo si vyžádal aktivaci účtu či obnovu hesla. */
export function TransactionalMonitor(): JSX.Element {
  const [brand, setBrand] = useState('');
  const [days, setDays] = useState(7);
  const stats = useQuery<TxStats>({
    queryKey: ['tx-stats', brand],
    queryFn: () => api.get(`/api/transactional/stats${qs({ brand })}`),
    refetchInterval: 30_000,
  });
  const creds = useQuery<{ days: number; codes: CredentialCode[] }>({
    queryKey: ['tx-credentials', brand, days],
    queryFn: () => api.get(`/api/transactional/credential-requests${qs({ brand, days })}`),
  });
  const s = stats.data;
  return (
    <>
      <Panel
        title="Posledních 24 hodin"
        subtitle="Co e-shop poslal a co s tím hub udělal. Prodleva je od přijetí do předání SmartEmailingu."
        action={<BrandSelect value={brand} onChange={setBrand} />}
      >
        {stats.isLoading ? <Loading what="čísla" /> : null}
        {stats.isError ? (
          <ErrorState
            title="Čísla se nepodařilo načíst"
            error={stats.error}
            onRetry={() => void stats.refetch()}
          />
        ) : null}
        {s ? (
          <div className="tiles" data-testid="tx-tiles">
            <Tile label="Přijato" value={num(s.accepted)} note={`Odesláno ${num(s.sent)}`} />
            <Tile
              label="Prodleva (medián)"
              value={lag(s.latencyP50Ms)}
              note={`95. percentil ${lag(s.latencyP95Ms)}`}
            />
            <Tile
              label="Zaseknuté"
              value={num(s.stuck)}
              bad={s.stuck > 0}
              note={s.stuck > 0 ? 'Čeká déle než 30 minut' : `Ve frontě ${num(s.queued)}`}
            />
            <Tile
              label="Neznámý výsledek"
              value={num(s.unknown)}
              bad={s.unknown > 0}
              note={`Selhalo ${num(s.failed)}`}
            />
            <Tile label="Zadrženo" value={num(s.held)} note={`Ve stínu ${num(s.shadow)}`} />
          </div>
        ) : null}
      </Panel>
      <Panel
        title="Kdo si vyžádal aktivaci účtu nebo obnovu hesla"
        subtitle="Počty žádostí z e-shopu. Odkazy a tokeny hub nikde neukládá ani nezobrazuje, adresy jsou zkrácené."
        action={
          <label className="field">
            <span>Období</span>
            <select value={days} onChange={(e) => setDays(Number(e.target.value))}>
              <option value={1}>1 den</option>
              <option value={7}>7 dní</option>
              <option value={30}>30 dní</option>
            </select>
          </label>
        }
      >
        {creds.isLoading ? <Loading what="žádosti" /> : null}
        {creds.isError ? (
          <ErrorState
            title="Žádosti se nepodařilo načíst"
            error={creds.error}
            onRetry={() => void creds.refetch()}
          />
        ) : null}
        {creds.data ? (
          <div className="scroll">
            <table data-testid="tx-credentials">
              <thead>
                <tr>
                  <th>Zpráva</th>
                  <th className="n">Žádostí</th>
                  <th>Proti minulému období</th>
                  <th>Stavy</th>
                  <th>Naposledy</th>
                  <th>Opakovaně (zkrácené adresy)</th>
                </tr>
              </thead>
              <tbody>
                {creds.data.codes.map((c) => (
                  <tr key={c.code}>
                    <td>
                      {c.name} <code>{c.code}</code>
                    </td>
                    <td className="n">{num(c.total)}</td>
                    <td>
                      {num(c.previousTotal)} — {TREND_TEXT[c.trend]}
                    </td>
                    <td>
                      {Object.entries(c.byStatus)
                        .map(([k, v]) => `${label(TX_STATUS_LABEL, k)} ${v}`)
                        .join(', ') || '—'}
                    </td>
                    <td>{c.lastAt ? dateTime(c.lastAt) : '—'}</td>
                    <td>
                      {c.repeated.length === 0
                        ? '—'
                        : c.repeated.map((r) => `${r.recipient} (${r.count}×)`).join(', ')}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : null}
      </Panel>
    </>
  );
}
